import { useState } from 'react';
import { matchSorter } from 'match-sorter';

// Types
import type { SocialProfileType } from '@/types/socialProfileType';

// Components
import { InputSearch } from '@/components/composite/SelectedProfiles/ProfilesList/AdditionalProfilesPopover/PopoverContent/InputSearch';
import { SocialProfileInfo } from '@/components/composite/SocialProfiles/Components/SocialProfileInfo';
import useTranslation from 'next-translate/useTranslation';
import useUserRole from '@/utils/hooks/user/useUserRole';

type PopoverContentProps = {
    profiles: SocialProfileType[];
    updateActive: (profile: SocialProfileType) => void;
};

export function PopoverContent({ profiles, updateActive }: PopoverContentProps) {
    const { t } = useTranslation('layout_platform');
    const { isAdmin } = useUserRole();
    const [filteredProfiles, setFilteredProfiles] = useState(profiles);

    const filterProfiles = (event: React.ChangeEvent<HTMLInputElement>) => {
        const searchValue = event.target.value;
        // Reset lists if the input is empty
        if (!searchValue) {
            setFilteredProfiles(profiles);
        }

        // Filter on Additional social profiles
        setFilteredProfiles(
            matchSorter(profiles, searchValue, {
                threshold: matchSorter.rankings.CONTAINS,
                keys: ['slug', 'social_network_type', 'external_api_data.name']
            })
        );
    };

    return (
        <div className="space-y-3.5 px-2 pb-4 pt-5">
            <InputSearch onChange={filterProfiles} />
            <div className="scrollbar flex h-60 flex-col gap-3.5 overflow-x-hidden px-3 pb-1 pt-2">
                {!filteredProfiles.length ? (
                    <div className="flex flex-1 items-center justify-center text-sm text-icoGray-500 dark:text-icoDarkMode-wolf">
                        {t('profiles.additional_profiles.search_no_profiles')}
                    </div>
                ) : null}
                {filteredProfiles.map((profile) => (
                    <button
                        key={profile.id}
                        className="flex w-full items-center gap-3 focus:ring-0"
                        onClick={() => updateActive(profile)}>
                        <SocialProfileInfo
                            profile={profile}
                            textSize="sm"
                            displayErrorWarning
                            isAdmin={isAdmin}
                        />
                    </button>
                ))}
            </div>
        </div>
    );
}
